/**
 * District names on the map, as DOM markers.
 *
 * Not a symbol layer: MapLibre's `text-field` needs a `glyphs` endpoint and this
 * style is raster-only by design (see useMapLibre). Adding a font-pbf host for
 * nineteen labels would put another origin in the CSP and another thing that can
 * be down in the render path.
 *
 * They stay up whether the choropleth is on or off. Their job is to say which
 * district the certificates under the cursor belong to, and that question
 * matters most precisely when the fill is off and the letter colours are showing
 * untinted.
 */

import type { Feature } from "geojson";
import maplibregl from "maplibre-gl";
import { useEffect, useRef } from "react";

import { areaContext, areaName } from "./lib/distritos";
import { count, num, str } from "./lib/props";

interface Label {
  key: string;
  el: HTMLButtonElement;
  marker: maplibregl.Marker;
  lngLat: [number, number];
  /** Measured once at mount: offsetWidth forces layout, and doing that for
   *  every label on every frame of a pan makes the map stutter. */
  w: number;
  h: number;
  /** Bigger districts win a collision. */
  priority: number;
}

/**
 * The opening fitBounds lands around z10.5 — València's term reaches down to El
 * Palmar — so the cutoff has to sit below that, or the city view would load with
 * no labels at all, exactly when the reader most needs to get their bearings.
 * Crowding at that zoom is handled by de-collision, not by hiding everything.
 */
const MIN_ZOOM = 9.5;

/** Breathing room around each label's box, in pixels. */
const PAD = 3;

export function useDistritoLabels(
  map: maplibregl.Map | null,
  ready: boolean,
  features: readonly Feature[] | null,
  selected: string | null,
  onSelect: (key: string) => void,
): void {
  const labels = useRef<Label[]>([]);
  // The handler is rebound on every render but the markers are mounted once, so
  // the element's listener reads the callback through a ref rather than closing
  // over the one that happened to be current at mount.
  const selectRef = useRef(onSelect);
  selectRef.current = onSelect;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  /**
   * Greedy de-collision: place by priority, drop any box overlapping one already
   * placed. Nineteen boxes, so the naive O(n²) pass is free.
   */
  const refresh = useRef(() => {});
  refresh.current = () => {
    if (!map || labels.current.length === 0) return;
    const tooFarOut = map.getZoom() < MIN_ZOOM;

    const order = [...labels.current].sort((a, b) => {
      // The open district always keeps its label.
      if (a.key === selectedRef.current) return -1;
      if (b.key === selectedRef.current) return 1;
      return b.priority - a.priority;
    });

    const placed: { x1: number; y1: number; x2: number; y2: number }[] = [];
    for (const label of order) {
      label.el.classList.toggle("selected", label.key === selectedRef.current);
      if (tooFarOut) {
        label.el.classList.add("suppressed");
        continue;
      }
      const pt = map.project(label.lngLat);
      const box = {
        x1: pt.x - label.w / 2 - PAD, y1: pt.y - label.h / 2 - PAD,
        x2: pt.x + label.w / 2 + PAD, y2: pt.y + label.h / 2 + PAD,
      };
      const clash = placed.some((b) =>
        !(box.x2 < b.x1 || box.x1 > b.x2 || box.y2 < b.y1 || box.y1 > b.y2));
      label.el.classList.toggle("suppressed", clash);
      if (!clash) placed.push(box);
    }
  };

  // Mount, once per district collection.
  useEffect(() => {
    if (!map || !ready || !features) return;

    for (const feature of features) {
      const p = feature.properties ?? {};
      const key = str(p["key"]);
      // The area's own name, whichever layer this is. Reading `distrito`
      // directly labelled all 88 barrios with their district's name.
      const name = areaName(p);
      const lng = num(p["label_lng"]);
      const lat = num(p["label_lat"]);
      if (!key || name === "–" || lng == null || lat == null) continue;

      const el = document.createElement("button");
      el.type = "button";
      el.className = "distrito-label";
      el.textContent = name;
      el.title = `${name} · ${areaContext(p)}`;
      el.addEventListener("click", (ev) => {
        ev.stopPropagation();
        selectRef.current(key);
      });

      const lngLat: [number, number] = [lng, lat];
      const marker = new maplibregl.Marker({ element: el, anchor: "center" })
        .setLngLat(lngLat)
        .addTo(map);

      labels.current.push({
        key, el, marker, lngLat,
        w: el.offsetWidth, h: el.offsetHeight,
        priority: count(p["total_edificios"]),
      });
    }

    refresh.current();

    return () => {
      for (const label of labels.current) label.marker.remove();
      labels.current = [];
    };
  }, [map, ready, features]);

  // Markers move with the map on their own; only the de-collision pass has to
  // re-run, throttled to one per animation frame.
  useEffect(() => {
    if (!map || !ready) return;

    let frame: number | null = null;
    const schedule = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        refresh.current();
      });
    };

    map.on("move", schedule);
    map.on("zoom", schedule);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      map.off("move", schedule);
      map.off("zoom", schedule);
    };
  }, [map, ready]);

  // A new selection changes both which label is highlighted and which one wins
  // a collision, so the pass re-runs outside the move handler too.
  useEffect(() => { refresh.current(); }, [selected]);
}
